
import React, { useState, useEffect } from 'react';
import { 
  PlusIcon, 
  TrashIcon, 
  CloudArrowUpIcon, 
  FireIcon,
  ClockIcon,
  DocumentPlusIcon,
  ChevronDownIcon // Added for the select arrow
} from '@heroicons/react/24/outline';
import adminApi from '../../api/adminApi';
import Toast from '../../components/ui/Toast';

const QuizManager = () => {
  // 1. STATE MANAGEMENT
  const [quizTitle, setQuizTitle] = useState('');
  const [bulkList, setBulkList] = useState([]);
  const [timeLimit, setTimeLimit] = useState(15);
  const [draftQuestions, setDraftQuestions] = useState([]);
  const [notification, setNotification] = useState(null);
  const [isDeploying, setIsDeploying] = useState(false);
  const [competitionsList, setCompetitionsList] = useState([]); // NEW: State for dropdown

  // Form State for the question builder
  const [formData, setFormData] = useState({
    question_text: '',
    options: ['', '', '', ''],
    correct_option_index: 0,
    points: 1
  });

  // --- NEW: FETCH COMPETITIONS ON MOUNT ---
  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        const res = await adminApi.getCompetitions();
        const data = res.competitions || res.data || [];
        
        // Filter: Show only Competitions (Exclude Internships)
        const onlyCompetitions = data.filter(item => 
          item.category?.toLowerCase() === 'competition' || 
          item.type?.toLowerCase() === 'competition'
        );
        
        setCompetitionsList(onlyCompetitions);
      } catch (err) {
        setNotification({ msg: "Failed to load programs", type: 'error' });
      }
    };
    fetchPrograms();
    fetchBulkList();

  }, []);


  // Fetch the bulk list of questions
    const fetchBulkList = async () => {
  try {
    const res = await adminApi.getQuizBulkList();
    setBulkList(res);
  } catch {
    console.error("Failed to fetch bulk list");
  }
};

  // 2. AUTO-RECOVERY LOGIC
  // Triggered when the dropdown selection changes
  const handleLoadExisting = async (selectedTitle) => {
    if (!selectedTitle) return;
    try {
      const target = competitionsList.find(c => c.title.toLowerCase() === selectedTitle.toLowerCase());
      
      if (target) {
        const existing = await adminApi.getQuizQuestions(target.id);
        if (existing && existing.length > 0) {
          const formatted = existing.map(q => ({
            ...q,
            options: typeof q.options === 'string' ? JSON.parse(q.options) : q.options
          }));
          setDraftQuestions(formatted);
          setNotification({ msg: `Loaded ${existing.length} existing questions`, type: 'success' });
        } else {
          // If no questions exist for this competition, clear the draft for a fresh start
          setDraftQuestions([]);
        }
      }
    } catch (err) {
 
      setDraftQuestions([]);
    }
  };

  // 3. DRAFT ACTIONS (Local Only)
  const addToDraft = (e) => {
    e.preventDefault();
    setDraftQuestions([...draftQuestions, { ...formData, id: `temp-${Date.now()}` }]);
    setFormData({ ...formData, question_text: '', options: ['', '', '', ''], correct_option_index: 0 });
  };

  const removeFromDraft = (id) => {
    setDraftQuestions(draftQuestions.filter(q => q.id !== id));
  };

  // 4. THE BIG DEPLOY (Database Hit)
  const deployToBackend = async () => {
    if (!quizTitle || draftQuestions.length === 0) {
      return setNotification({ msg: "Title and at least 1 question required!", type: 'error' });
    }

    setIsDeploying(true);
    try {
      const payload = {
        title: quizTitle,
        timeLimit: parseInt(timeLimit),
        questions: draftQuestions
      };
      
      await adminApi.bulkUploadQuiz(payload); 
      setNotification({ msg: "Deployment Successful! Arena is live.", type: 'success' });

      await fetchBulkList(); // fetch the builk list 

    } catch (err) {
      setNotification({ msg: err.response?.data?.message || "Deployment failed", type: 'error' });
    } finally {
      setIsDeploying(false);
    }
  };

  const handleBulkDelete = async (competitionId) => {
  const confirmDelete = window.confirm(
    "Are you sure you want to permanently delete this entire quiz bulk?"
  );

  if (!confirmDelete) return;

  try {
    await adminApi.deleteQuizBulk(competitionId);
    setNotification({ msg: "Bulk deleted successfully", type: "success" });

    // Refresh list
    const updated = await adminApi.getQuizBulkList();
    setBulkList(updated);

  } catch (err) {
    setNotification({ msg: "Deletion failed", type: "error" });
  }
};

  return (
    <div className="space-y-6">
      {notification && <Toast message={notification.msg} type={notification.type} onClose={() => setNotification(null)} />}

      {/* HEADER: CONFIGURATION */}
      <div className="bg-gray-800/50 p-6 rounded-2xl border border-gray-700 grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
        <div className="md:col-span-1">
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <FireIcon className="h-8 w-8 text-orange-500" /> Quiz Master
          </h1>
          <p className="text-gray-400 text-sm italic">Status: {draftQuestions.length > 0 ? 'Drafting' : 'Idle'}</p>
        </div>
        
        {/* REPLACED INPUT WITH SELECT */}
        <div className="relative">
          <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">Target Competition</label>
          <div className="relative mt-1">
            <select 
              className="w-full bg-gray-900 border border-gray-700 rounded-xl px-4 py-2 text-white focus:border-orange-500 outline-none appearance-none cursor-pointer transition-all pr-10"
              value={quizTitle}
              onChange={(e) => {
                const val = e.target.value;
                setQuizTitle(val);
                handleLoadExisting(val); // Auto-load existing questions immediately on change
              }}
            >
              <option value="">-- SELECT COMPETITION --</option>
              {competitionsList.map((comp) => (
                <option key={comp.id} value={comp.title}>
                  {comp.title.toUpperCase()}
                </option>
              ))}
            </select>
            <ChevronDownIcon className="h-4 w-4 text-gray-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>
        </div>

        <div>
          <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Time Per Question (Seconds)</label>
          <div className="flex items-center gap-2 mt-1">
            <ClockIcon className="h-5 w-5 text-orange-500" />
            <input 
              type="number"
              className="w-full bg-gray-900 border border-gray-700 rounded-xl px-4 py-2 text-white outline-none focus:border-orange-500"
              value={timeLimit}
              onChange={(e) => setTimeLimit(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* LEFT: THE BUILDER */}
        <form onSubmit={addToDraft} className="bg-gray-800/20 p-6 rounded-3xl border border-gray-700/50 space-y-4 h-fit sticky top-6">
          <div className="flex justify-between items-center border-b border-gray-700 pb-4">
            <h3 className="text-lg font-bold text-blue-400">Question Builder</h3>
            <span className="text-xs bg-blue-500/10 text-blue-400 px-2 py-1 rounded">Q#{draftQuestions.length + 1}</span>
          </div>

          <textarea 
            className="w-full bg-gray-900/50 border border-gray-700 rounded-2xl p-4 text-white focus:ring-2 ring-blue-500/20 outline-none"
            rows="3"
            required
            placeholder="Type your question here..."
            value={formData.question_text}
            onChange={(e) => setFormData({...formData, question_text: e.target.value})}
          />

          <div className="space-y-3">
            <label className="text-[10px] font-bold text-gray-500 uppercase ml-1">Options & Correct Answer</label>
            {formData.options.map((opt, idx) => (
              <div key={idx} className="flex items-center gap-3 group">
                <input 
                  type="radio" 
                  name="correct_draft" 
                  className="w-4 h-4 accent-blue-500 cursor-pointer"
                  checked={formData.correct_option_index === idx}
                  onChange={() => setFormData({...formData, correct_option_index: idx})}
                />
                <input 
                  type="text" 
                  required
                  className="flex-1 bg-gray-900 border border-gray-700 rounded-xl px-4 py-2 text-white text-sm focus:border-blue-500 transition-colors"
                  placeholder={`Choice ${idx + 1}`}
                  value={opt}
                  onChange={(e) => {
                    const newOpts = [...formData.options];
                    newOpts[idx] = e.target.value;
                    setFormData({...formData, options: newOpts});
                  }}
                />
              </div>
            ))}
          </div>

          <button type="submit" className="w-full bg-blue-600/10 hover:bg-blue-600 text-blue-400 hover:text-white font-bold py-4 rounded-2xl flex items-center justify-center gap-2 transition-all border border-blue-500/30">
            <PlusIcon className="h-5 w-5" /> Append to Draft
          </button>
        </form>

        {/* RIGHT: THE REVIEW BOARD */}
        <div className="space-y-4">
          <div className="flex items-center justify-between px-2">
            <h3 className="text-lg font-bold text-gray-300">Live Draft Review</h3>
            <button 
              onClick={deployToBackend}
              disabled={isDeploying}
              className={`px-6 py-2 rounded-xl font-bold flex items-center gap-2 transition-all shadow-lg ${
                isDeploying ? 'bg-gray-700 text-gray-500' : 'bg-orange-600 hover:bg-orange-500 text-white shadow-orange-600/20 hover:scale-105'
              }`}
            >
              <CloudArrowUpIcon className="h-5 w-5" /> {isDeploying ? 'Deploying...' : 'DEPLOY ARENA'}
            </button>
          </div>

          <div className="bg-gray-900/40 border border-gray-700/50 rounded-3xl p-2 max-h-[650px] overflow-y-auto custom-scrollbar space-y-3">
            {draftQuestions.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-gray-600 space-y-4">
                <DocumentPlusIcon className="h-12 w-12 opacity-20" />
                <p className="italic font-mono text-sm">// Your draft board is empty</p>
              </div>
            ) : (
              draftQuestions.map((q, i) => (
                <div key={q.id} className="bg-gray-800/40 p-5 rounded-2xl border border-gray-700 relative group hover:border-blue-500/50 transition-all">
                  <div className="flex gap-4">
                    <span className="text-blue-500 font-black text-lg opacity-50">#{(i+1).toString().padStart(2, '0')}</span>
                    <p className="text-sm font-semibold text-white leading-relaxed pr-6">{q.question_text}</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2 mt-4">
                    {q.options.map((opt, idx) => (
                      <div key={idx} className={`text-[10px] p-2 rounded-lg border ${
                        idx === q.correct_option_index 
                        ? 'bg-green-500/10 border-green-500/50 text-green-400 font-bold' 
                        : 'bg-gray-900/50 border-gray-800 text-gray-500'
                      }`}>
                        {opt}
                      </div>
                    ))}
                  </div>
                  
                  <button 
                    onClick={() => removeFromDraft(q.id)}
                    className="absolute top-4 right-4 p-2 text-gray-600 hover:text-red-500 hover:bg-red-500/10 rounded-full transition-all"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              ))
            )}
          </div>

          {draftQuestions.length > 0 && (
            <div className="flex justify-center gap-6 pt-2">
              <button 
                onClick={() => { if(window.confirm("Nuke entire draft?")) setDraftQuestions([]) }}
                className="text-gray-500 text-[10px] hover:text-red-400 flex items-center gap-1 transition-colors uppercase tracking-widest font-bold"
              >
                <TrashIcon className="h-3 w-3" /> Clear Draft
              </button>
            </div>
          )}

          {/* BULK QUIZ LIST SECTION */}
<div className="mt-12 bg-gray-900/40 border border-gray-700/50 rounded-3xl p-6">
  <h3 className="text-lg font-bold text-red-400 mb-6">
    Uploaded Quiz Bulks
  </h3>

  <div className="max-h-[300px] overflow-y-auto space-y-4">
    {bulkList.length === 0 ? (
      <p className="text-gray-500 text-sm italic">
        No bulk quizzes uploaded yet.
      </p>
    ) : (
      bulkList.map((item) => (
        <div
          key={item.competition_id}
          className="flex justify-between items-center bg-gray-800/50 border border-gray-700 rounded-xl px-4 py-3"
        >
          <div>
            <p className="text-white font-semibold">
              {item.title}
            </p>
            <p className="text-gray-500 text-xs">
              {item.total_questions} Questions
            </p>
          </div>

          <button
            onClick={() => handleBulkDelete(item.competition_id)}
            className="text-red-400 hover:text-red-300 text-sm font-bold transition"
          >
            DELETE
          </button>
        </div>
      ))
    )}
  </div>
</div>
        </div>
      </div>
    </div>
  );
};

export default QuizManager;